import { localStateStore } from '@/utils/localStateStore';

type PlayableSong = {
  _id: string;
  title?: string;
  name?: string;
  artist?: string;
  filePath?: string;
  duration?: number;
  cdnUrl?: string;
  artwork?: string;
  isAnnouncement?: boolean;
  announcementId?: string;
  audioFileId?: string;
  announcementScheduleType?: 'songs' | 'minutes' | 'specific';
  scheduledAnnouncementKey?: string;
};

type PlaylistLike<TSong extends PlayableSong> = {
  _id: string;
  name: string;
  songs: TSong[];
  artwork?: string;
  baseUrl?: string;
  isScheduled?: boolean;
  originalId?: string;
  scheduleId?: string;
};

type AnnouncementAudioFile = {
  url: string;
  status?: string;
  localPath?: string;
  id?: string;
  _id?: string;
  fileName?: string;
};

type AnnouncementLike = {
  _id: string;
  title?: string;
  content?: string;
  scheduleType?: 'songs' | 'minutes' | 'specific';
  songInterval?: number;
  audioFile?: AnnouncementAudioFile[];
  audioFiles?: AnnouncementAudioFile[];
};

type RotationState = Record<string, number>;
type CounterState = Record<string, number>;

type BuildOptions<TAnnouncement extends AnnouncementLike> = {
  announcements?: TAnnouncement[];
  counters: CounterState;
  sameIntervalRotation: RotationState;
  audioRotation: RotationState;
  isAnnouncementActive: (announcement: TAnnouncement) => boolean;
  preserveSongOrder?: boolean;
};

const PLAYBACK_HISTORY_KEY = 'enterprisePlaybackHistory';
const MAX_HISTORY_DAYS = 14;

function getDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getSongKey(song: PlayableSong): string {
  return song._id || song.cdnUrl || song.filePath || song.name || song.title || '';
}

function hashString(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createSeededRandom(seedText: string) {
  let seed = hashString(seedText) || 1;
  return () => {
    seed += 0x6D2B79F5;
    let value = seed;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle<T>(items: T[], seedText: string): T[] {
  const result = [...items];
  const random = createSeededRandom(seedText);

  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }

  return result;
}

function readHistory(): Record<string, Record<string, string[]>> {
  try {
    const raw = localStateStore.getString(PLAYBACK_HISTORY_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeHistory(history: Record<string, Record<string, string[]>>): void {
  try {
    const dateKeys = Object.keys(history).sort();
    while (dateKeys.length > MAX_HISTORY_DAYS) {
      const oldest = dateKeys.shift();
      if (oldest) delete history[oldest];
    }

    localStateStore.setJson(PLAYBACK_HISTORY_KEY, history);
  } catch {
    // Storage dolu/kapaliysa player calmayi surdursun.
  }
}

function getPlaylistHistoryKey(playlistId: string): string {
  return playlistId.replace(/^scheduled_/, '');
}

export function recordEnterpriseSongPlay(playlistId: string, song?: PlayableSong | null): void {
  if (!playlistId || !song || song.isAnnouncement) return;

  const songKey = getSongKey(song);
  if (!songKey) return;

  const dateKey = getDateKey();
  const playlistKey = getPlaylistHistoryKey(playlistId);
  const history = readHistory();
  const dayHistory = history[dateKey] || {};
  const playedSongs = dayHistory[playlistKey] || [];

  if (!playedSongs.includes(songKey)) {
    dayHistory[playlistKey] = [...playedSongs, songKey];
    history[dateKey] = dayHistory;
    writeHistory(history);
  }
}

function orderSongsForRadio<TSong extends PlayableSong>(
  playlistId: string,
  songs: TSong[],
  preserveSongOrder = false
): TSong[] {
  if (preserveSongOrder || songs.length <= 1) {
    return [...songs];
  }

  const dateKey = getDateKey();
  const playlistKey = getPlaylistHistoryKey(playlistId);
  const history = readHistory();
  const playedToday = new Set(history[dateKey]?.[playlistKey] || []);
  const cycle = Math.floor(playedToday.size / Math.max(songs.length, 1));
  const shuffled = seededShuffle(songs, `${playlistKey}:${dateKey}:${cycle}`);

  const unplayed = shuffled.filter(song => !playedToday.has(getSongKey(song)));
  const played = shuffled.filter(song => playedToday.has(getSongKey(song)));

  return unplayed.length > 0 ? [...unplayed, ...played] : shuffled;
}

function selectDueAnnouncement<TAnnouncement extends AnnouncementLike>(
  dueAnnouncements: TAnnouncement[],
  sameIntervalRotation: RotationState
): TAnnouncement {
  if (dueAnnouncements.length === 1) {
    return dueAnnouncements[0];
  }

  const maxInterval = Math.max(...dueAnnouncements.map(item => item.songInterval || 1));
  const candidates = dueAnnouncements.filter(item => item.songInterval === maxInterval);

  if (candidates.length === 1) {
    return candidates[0];
  }

  const rotationKey = `interval_${maxInterval}`;
  const nextIndex = ((sameIntervalRotation[rotationKey] ?? -1) + 1) % candidates.length;
  sameIntervalRotation[rotationKey] = nextIndex;

  return candidates[nextIndex];
}

function createAnnouncementTrack<TSong extends PlayableSong, TAnnouncement extends AnnouncementLike>(
  announcement: TAnnouncement,
  audioRotation: RotationState
): TSong | null {
  const files = Array.isArray(announcement.audioFile) && announcement.audioFile.length > 0
    ? announcement.audioFile
    : (announcement.audioFiles || []);
  const approvedFiles = files.filter(file => file?.url && (file.status === 'approved' || !file.status));

  if (approvedFiles.length === 0) {
    return null;
  }

  const currentIndex = audioRotation[announcement._id] ?? -1;
  const nextIndex = (currentIndex + 1) % approvedFiles.length;
  audioRotation[announcement._id] = nextIndex;

  const selectedAudioFile = approvedFiles[nextIndex];

  return {
    _id: `announcement_${announcement._id}_${nextIndex}_${Date.now()}`,
    title: announcement.title || 'Anons',
    name: announcement.title || 'Anons',
    artist: announcement.content ? announcement.content.substring(0, 30) : 'Anons',
    filePath: selectedAudioFile.url,
    cdnUrl: selectedAudioFile.url,
    duration: 30,
    isAnnouncement: true,
    announcementId: announcement._id,
    audioFileId: selectedAudioFile.id || selectedAudioFile._id || selectedAudioFile.url
  } as TSong;
}

export function buildEnterprisePlaylist<
  TSong extends PlayableSong,
  TPlaylist extends PlaylistLike<TSong>,
  TAnnouncement extends AnnouncementLike
>(playlist: TPlaylist, options: BuildOptions<TAnnouncement>): TPlaylist {
  const baseSongs = (playlist.songs || []).filter(song => !song.isAnnouncement);
  const orderedSongs = orderSongsForRadio(playlist._id, baseSongs, options.preserveSongOrder);
  const songAnnouncements = (options.announcements || [])
    .filter(announcement =>
      announcement.scheduleType === 'songs' &&
      typeof announcement.songInterval === 'number' &&
      announcement.songInterval > 0 &&
      options.isAnnouncementActive(announcement)
    );

  if (songAnnouncements.length === 0) {
    return {
      ...playlist,
      songs: orderedSongs
    };
  }

  songAnnouncements.forEach(announcement => {
    if (options.counters[announcement._id] === undefined) {
      options.counters[announcement._id] = 0;
    }
  });

  const enhancedSongs: TSong[] = [];

  orderedSongs.forEach(song => {
    enhancedSongs.push(song);

    songAnnouncements.forEach(announcement => {
      options.counters[announcement._id] += 1;
    });

    const dueAnnouncements = songAnnouncements.filter(announcement => {
      const interval = announcement.songInterval || 1;
      return options.counters[announcement._id] % interval === 0;
    });

    if (dueAnnouncements.length === 0) {
      return;
    }

    const selectedAnnouncement = selectDueAnnouncement(dueAnnouncements, options.sameIntervalRotation);
    const announcementTrack = createAnnouncementTrack<TSong, TAnnouncement>(
      selectedAnnouncement,
      options.audioRotation
    );

    if (announcementTrack) {
      enhancedSongs.push(announcementTrack);
    }
  });

  return {
    ...playlist,
    songs: enhancedSongs
  };
}
